import { useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { MoreSearchIcon, DiscardSearchIcon } from "components/shared/icons";
import { DIRECTION } from "services/general";
import { CATEGORIES, getPermittedModuleKeys } from "./searchConfig";

const SCROLL_STEP = 200;
const SCROLL_THRESHOLD = 2;

const CategoryTabs = ({ activeCategories = [], onCategoryChange }) => {
  const { t } = useTranslation();
  const scrollRef = useRef(null);

  const isAllMode = activeCategories.length === 0;

  const permittedKeys = useMemo(() => new Set(getPermittedModuleKeys()), []);

  const moduleCategories = [
    { key: CATEGORIES.PROJECTS, label: t("globalSearch.categories.projects") },
    {
      key: CATEGORIES.PROPERTIES,
      label: t("globalSearch.categories.properties"),
    },
    { key: CATEGORIES.UNITS, label: t("globalSearch.categories.units") },
    { key: CATEGORIES.INVOICES, label: t("globalSearch.categories.invoices") },
    {
      key: CATEGORIES.CUSTOMERS,
      label: t("globalSearch.categories.customers"),
    },
    { key: CATEGORIES.ENTITIES, label: t("globalSearch.categories.entities") },
    { key: CATEGORIES.VOUCHERS, label: t("globalSearch.categories.vouchers") },
    {
      key: CATEGORIES.LEAD_CONTACTS,
      label: t("globalSearch.categories.leadContacts"),
    },
    {
      key: CATEGORIES.CONTRACTS,
      label: t("globalSearch.categories.contracts"),
    },
    { key: CATEGORIES.PRODUCTS, label: t("globalSearch.categories.products") },
    {
      key: CATEGORIES.SUPPORT_CENTER,
      label: t("globalSearch.categories.supportCenter"),
    },
    {
      key: CATEGORIES.UNIT_MESSAGES,
      label: t("globalSearch.categories.unitMessages"),
    },
  ].filter((category) => permittedKeys.has(category.key));

  const activeSet = new Set(activeCategories);

  const scrollForward = () => {
    const el = scrollRef.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    const maxScroll = scrollWidth - clientWidth;
    if (maxScroll <= 0) return;

    const isRtl = DIRECTION === "rtl";
    const atEnd = isRtl
      ? scrollLeft <= -maxScroll + SCROLL_THRESHOLD
      : scrollLeft >= maxScroll - SCROLL_THRESHOLD;

    if (atEnd) {
      el.scrollLeft = 0;
    } else {
      const amount = isRtl ? -SCROLL_STEP : SCROLL_STEP;
      el.scrollBy({ left: amount, behavior: "smooth" });
    }
  };

  const chipBase =
    "px-2.5 sm:px-3 md:px-4 py-1.5 sm:py-2 rounded-full text-xs sm:text-sm font-medium whitespace-nowrap transition-colors duration-200 flex-shrink-0 flex items-center gap-1";
  const activeModuleChip = "border border-brand bg-surface text-[#2F2D38]";
  const inactiveChip =
    "bg-surface text-[#2F2D38] border border-[#EDEDED] hover:bg-surface-muted";

  const navButtonClass =
    "shrink-0 flex items-center justify-center w-8 h-8 rounded-full text-brand transition-colors hover:opacity-80";

  return (
    <div className="flex items-center gap-1.5 sm:gap-2 pb-2">
      <div
        ref={scrollRef}
        className="flex-1 min-w-0 overflow-x-auto scrollbar-hidden"
        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
      >
        <div className="flex gap-1.5 sm:gap-2">
          {isAllMode ? (
            <button
              type="button"
              onClick={() => onCategoryChange(CATEGORIES.ALL)}
              className={`${chipBase} ${activeModuleChip}`}
            >
              {t("globalSearch.categories.all")}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => onCategoryChange(CATEGORIES.ALL)}
              className={`${chipBase} ${inactiveChip}`}
              aria-label={t("globalSearch.removeAll")}
            >
              {t("globalSearch.removeAll")}
              <span
                className="inline-flex items-center justify-center shrink-0"
                aria-hidden="true"
              >
                <DiscardSearchIcon />
              </span>
            </button>
          )}
          {moduleCategories.map((category) => {
            const selected = activeSet.has(category.key);
            return (
              <button
                key={category.key}
                type="button"
                onClick={() => onCategoryChange(category.key)}
                className={`${chipBase} ${selected ? activeModuleChip : inactiveChip}`}
              >
                {category.label}
              </button>
            );
          })}
        </div>
      </div>
      <button
        type="button"
        onClick={scrollForward}
        className={navButtonClass}
        aria-label={t("globalSearch.next")}
      >
        <span
          className={
            DIRECTION === "ltr" ? "rotate-180 inline-flex" : "inline-flex"
          }
        >
          <MoreSearchIcon />
        </span>
      </button>
    </div>
  );
};

export default CategoryTabs;
